/**
 * Date utility functions for WalkCompanion
 */

import { format, isToday, isYesterday, isTomorrow, startOfDay, endOfDay, addDays, subDays, differenceInDays } from 'date-fns';

/**
 * Format a date string to a readable format
 */
export function formatDate(date: string | Date, formatString: string = 'MMM dd, yyyy'): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, formatString);
}

/**
 * Format a date for display in the UI
 */
export function formatDisplayDate(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isToday(dateObj)) {
    return 'Today';
  } else if (isYesterday(dateObj)) {
    return 'Yesterday';
  } else if (isTomorrow(dateObj)) {
    return 'Tomorrow';
  } else {
    return format(dateObj, 'MMM dd');
  }
}

/**
 * Format a date for database storage (ISO string)
 */
export function formatDateForDB(date: Date): string {
  return date.toISOString();
}

/**
 * Get today's date as ISO string
 */
export function getTodayISO(): string {
  const parts = new Date().toISOString().split('T');
  return parts[0] || new Date().toISOString().substring(0, 10);
}

/**
 * Check if a date is today
 */
export function isDateToday(date: string | Date): boolean {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return isToday(dateObj);
}

/**
 * Check if a date is in the past
 */
export function isDateInPast(date: string | Date): boolean {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj < new Date();
}

/**
 * Check if a date is in the future
 */
export function isDateInFuture(date: string | Date): boolean {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj > new Date();
}

/**
 * Check if a date is within a range
 */
export function isDateInRange(date: string | Date, startDate: string | Date, endDate: string | Date): boolean {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const startObj = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const endObj = typeof endDate === 'string' ? new Date(endDate) : endDate;

  return dateObj >= startObj && dateObj <= endObj;
}

/**
 * Get the number of days between two dates
 */
export function getDaysBetween(startDate: string | Date, endDate: string | Date): number {
  const startObj = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const endObj = typeof endDate === 'string' ? new Date(endDate) : endDate;

  return differenceInDays(endObj, startObj);
}

/**
 * Calculate days remaining until a given date
 */
export function getDaysRemaining(endDate: string | null | undefined): number {
  if (!endDate) return 0;

  const end = new Date(endDate);
  const now = new Date();
  const diffTime = end.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

/**
 * Format challenge end date for display
 */
export function formatChallengeEndDate(endDate: string | null | undefined): string {
  if (!endDate) return "Ongoing";

  const date = new Date(endDate);
  const options: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  };
  return date.toLocaleDateString('en-US', options);
}

/**
 * Get challenge time status (days remaining, ended, etc.)
 */
export function getChallengeTimeStatus(startDate: string | null | undefined, endDate: string | null | undefined): {
  status: 'upcoming' | 'active' | 'ended' | 'ongoing';
  daysRemaining: number;
  daysSinceStart: number;
  formattedEndDate: string;
  hasValidDates: boolean;
} {
  // If no dates are provided, treat as ongoing
  if (!startDate && !endDate) {
    return {
      status: 'ongoing',
      daysRemaining: 0,
      daysSinceStart: 0,
      formattedEndDate: "Ongoing",
      hasValidDates: false
    };
  }

  const now = new Date();
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;

  const daysRemaining = getDaysRemaining(endDate);
  const daysSinceStart = start ? Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) : 0;

  let status: 'upcoming' | 'active' | 'ended' | 'ongoing';

  if (!start && !end) {
    status = 'ongoing';
  } else if (start && now < start) {
    status = 'upcoming';
  } else if (end && now > end) {
    status = 'ended';
  } else {
    status = 'active';
  }

  return {
    status,
    daysRemaining,
    daysSinceStart,
    formattedEndDate: formatChallengeEndDate(endDate),
    hasValidDates: !!(startDate || endDate)
  };
}

/**
 * Format relative time (e.g., "2 days ago", "in 3 days")
 */
export function formatRelativeTime(date: string | null | undefined): string {
  if (!date) return "ongoing";

  const targetDate = new Date(date);
  const now = new Date();
  const diffTime = targetDate.getTime() - now.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'today';
  } else if (diffDays === 1) {
    return 'tomorrow';
  } else if (diffDays === -1) {
    return 'yesterday';
  } else if (diffDays > 0) {
    return `in ${diffDays} days`;
  } else {
    return `${Math.abs(diffDays)} days ago`;
  }
}

/**
 * Get a user-friendly message for challenge timing
 */
export function getChallengeTimingMessage(startDate: string | null | undefined, endDate: string | null | undefined): string {
  const timeStatus = getChallengeTimeStatus(startDate, endDate);

  if (!timeStatus.hasValidDates) {
    return "Use common sense - complete daily";
  }

  switch (timeStatus.status) {
    case 'upcoming':
      return `Starts ${formatRelativeTime(startDate)}`;
    case 'active':
      if (timeStatus.daysRemaining > 0) {
        return `${timeStatus.daysRemaining} days remaining`;
      } else {
        return "Ends today";
      }
    case 'ended':
      return "Challenge ended";
    case 'ongoing':
      return "Ongoing challenge";
    default:
      return "Use common sense - complete daily";
  }
}

/**
 * Get the start of a day
 */
export function getStartOfDay(date: string | Date): Date {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return startOfDay(dateObj);
}

/**
 * Get the end of a day
 */
export function getEndOfDay(date: string | Date): Date {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return endOfDay(dateObj);
}

/**
 * Add days to a date
 */
export function addDaysToDate(date: string | Date, days: number): Date {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return addDays(dateObj, days);
}

/**
 * Subtract days from a date
 */
export function subtractDaysFromDate(date: string | Date, days: number): Date {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return subDays(dateObj, days);
}

/**
 * Get a date range for the last N days
 */
export function getLastNDays(n: number): { startDate: string; endDate: string } {
  const endDate = new Date();
  const startDate = subDays(endDate, n - 1);

  return {
    startDate: format(startDate, 'yyyy-MM-dd'),
    endDate: format(endDate, 'yyyy-MM-dd')
  };
}

/**
 * Get a date range for the current week
 */
export function getCurrentWeek(): { startDate: string; endDate: string } {
  const today = new Date();
  const startOfWeek = startOfDay(subDays(today, today.getDay()));
  const endOfWeek = endOfDay(addDays(startOfWeek, 6));

  return {
    startDate: format(startOfWeek, 'yyyy-MM-dd'),
    endDate: format(endOfWeek, 'yyyy-MM-dd')
  };
}

/**
 * Get a date range for the current month
 */
export function getCurrentMonth(): { startDate: string; endDate: string } {
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  return {
    startDate: format(startOfMonth, 'yyyy-MM-dd'),
    endDate: format(endOfMonth, 'yyyy-MM-dd')
  };
}

/**
 * Parse an ISO date string and return a Date object
 */
export function parseISODate(dateString: string): Date {
  return new Date(dateString);
}

/**
 * Validate if a string is a valid date
 */
export function isValidDate(dateString: string): boolean {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
}

/**
 * Get relative time string (e.g., "2 hours ago", "3 days ago")
 */
export function getRelativeTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInMs = now.getTime() - dateObj.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 1) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
  } else {
    return format(dateObj, 'MMM dd, yyyy');
  }
}

/**
 * Check if a date is within the grace period (5 days in the past)
 */
export function isWithinGracePeriod(date: Date): boolean {
  const today = new Date();
  const gracePeriodStart = new Date(today);
  gracePeriodStart.setDate(today.getDate() - 5); // 5 days ago

  // Reset time to start of day for accurate comparison
  const dateStart = new Date(date);
  dateStart.setHours(0, 0, 0, 0);

  const graceStart = new Date(gracePeriodStart);
  graceStart.setHours(0, 0, 0, 0);

  const todayStart = new Date(today);
  todayStart.setHours(0, 0, 0, 0);

  return dateStart >= graceStart && dateStart <= todayStart;
}

/**
 * Get the grace period date range (5 days ago to today)
 */
export function getGracePeriodRange(): { start: Date; end: Date } {
  const today = new Date();
  const gracePeriodStart = new Date(today);
  gracePeriodStart.setDate(today.getDate() - 5); // 5 days ago

  return {
    start: gracePeriodStart,
    end: today
  };
}

/**
 * Check if a date is in the past (including today)
 */
export function isPastOrToday(date: Date): boolean {
  const today = new Date();
  const dateStart = new Date(date);
  dateStart.setHours(0, 0, 0, 0);

  const todayStart = new Date(today);
  todayStart.setHours(0, 0, 0, 0);

  return dateStart <= todayStart;
}

/**
 * Format a date for display in the calendar
 */
export function formatCalendarDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    weekday: 'short'
  });
} 